import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProblemNetworkVisualization from '../components/ProblemNetworkVisualization';
import NavBar from '../components/NavBar';
import ResponsiveFooter from '../components/ResponsiveFooter';

// EXACT colors from LandingPage.jsx - only blue, black, white
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const ProblemDetailsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col" 
         style={{
           color: colors.gray900,
           fontFamily: fontStyles.fontFamily
         }}>
      
      {/* Navigation */}
      <NavBar activePage="" />
      
      {/* Header with back button and main heading */}
      <motion.div 
        className="pt-28 pb-16 px-6"
        style={{
          background: `radial-gradient(circle at 70% 20%, ${colors.accentLight} 0%, rgba(255, 255, 255, 0) 25%)`,
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto max-w-6xl">
          <motion.button
            onClick={() => navigate('/')}
            className="flex items-center text-sm mb-8 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
            style={{ color: colors.gray700 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </motion.button>

          {/* Main Heading Section */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6" 
              style={{
                backgroundColor: colors.accentLight, 
                color: colors.accent,
                ...fontStyles.medium
              }}
            >
              The Contract Workflow Crisis
            </motion.div>
            
            <motion.h2 
              className="text-4xl md:text-6xl font-bold mb-6" 
              style={{
                ...fontStyles.heading,
                letterSpacing: '-0.04em',
                lineHeight: '1.1',
                color: colors.gray900
              }}
            >
              The <span style={{color: colors.accent}}>complete breakdown</span><br />
              of contract chaos
            </motion.h2>
            
            <motion.p 
              className="text-xl max-w-4xl mx-auto"
              style={{
                color: colors.gray700,
                ...fontStyles.body,
                lineHeight: '1.5'
              }}
            >
              Step-by-step analysis of why every contract workflow becomes an expensive nightmare
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Problem Network Visualization */}
      <ProblemNetworkVisualization />
      
      {/* Footer */}
      <ResponsiveFooter />
    </div>
  );
};

export default ProblemDetailsPage; 